import { CustomFieldType, RuleTargetAction, RuleTargetType } from '@ee/common/enums';
import { Operator } from './custom-field.model';

/**
 * A rule on the **case template**, not on a form. That single move is what lets the case panel honour the
 * same rule the wizard honours.
 *
 * Flat only: a conjunction above a repeated condition row. Nesting is structurally impossible, which is
 * deliberate — a rule nobody can read at a glance is a rule nobody trusts.
 */
export type RuleScope = 'INTAKE' | 'CASE' | 'EVERYWHERE';

export type RuleConjunction = 'ALL' | 'ANY';

/** Requiredness is two named gates, and a rule may set either. */
export type RequirednessGate = 'SUBMIT' | 'WORKFLOW';

export interface LayoutRuleCondition {
  field_key: string | null;
  /** The existing wire vocabulary. The friendly operators below map onto it. */
  operator: Operator | null;
  /** Kept in its raw type, so `is greater than` on a currency field compares numbers. */
  value?: unknown;
}

export interface LayoutRuleTarget {
  kind: RuleTargetType;
  /** A field's storage key, or a section's stable key. */
  ref: string;
}

export interface LayoutRule {
  id?: string;
  name?: string;
  active: boolean;
  /** Evaluation is scoped by this, never by section name. */
  case_type_id?: string | null;
  conjunction: RuleConjunction;
  conditions: LayoutRuleCondition[];
  action: RuleTargetAction;
  targets: LayoutRuleTarget[];
  scope: RuleScope;
  requiredness_gate?: RequirednessGate;
  /** Shown in place next to the field: "Asked because no rent amount is entered". */
  reason?: string | null;
}

export const RULE_SCOPE_LABELS: Record<RuleScope, string> = {
  INTAKE: 'Intake only',
  CASE: 'Case only',
  EVERYWHERE: 'Everywhere'
};

/**
 * What each scope actually does, stated where the choice is made. The behaviour difference is the whole
 * reason scope exists, and it is not guessable from three words.
 */
export const RULE_SCOPE_HINTS: Record<RuleScope, string> = {
  INTAKE: 'Skipped in the wizard. Your team still sees it on the case.',
  CASE: 'Asked at intake, then hidden afterwards.',
  EVERYWHERE: 'Skipped in the wizard and collapsed on the case panel.'
};

export interface FriendlyOperator {
  /** What the author reads. */
  label: string;
  /** What goes on the wire — the existing `Operator` union, not a second vocabulary. */
  operator: Operator;
  /** `is empty` / `is provided` take no value, so the control disappears entirely. */
  takesValue: boolean;
  /** True when the value control should be a dropdown over the field's known options. */
  fromOptions?: boolean;
}

const IS: FriendlyOperator = { label: 'is', operator: '==', takesValue: true, fromOptions: true };
const IS_ONE_OF: FriendlyOperator = { label: 'is one of', operator: 'in', takesValue: true, fromOptions: true };
const IS_NOT: FriendlyOperator = { label: 'is not', operator: 'not_contains', takesValue: true, fromOptions: true };
const IS_EMPTY: FriendlyOperator = { label: 'is empty', operator: 'blank', takesValue: false };
const IS_PROVIDED: FriendlyOperator = { label: 'is provided', operator: 'none', takesValue: false };

/**
 * The operator list follows the field's type. Offering *is before* on a currency field, or
 * *is greater than* on a dropdown, is how a builder teaches people that it does not understand its own
 * data.
 */
export const OPERATORS_BY_TYPE: Partial<Record<CustomFieldType, FriendlyOperator[]>> = {
  [CustomFieldType.SELECT]: [IS, IS_ONE_OF, IS_NOT, IS_EMPTY],
  [CustomFieldType.RADIO]: [IS, IS_ONE_OF, IS_NOT, IS_EMPTY],
  [CustomFieldType.TAGS]: [IS_ONE_OF, IS_EMPTY],
  [CustomFieldType.TEXT]: [
    IS,
    { label: 'contains', operator: 'contains', takesValue: true },
    IS_EMPTY
  ],
  [CustomFieldType.MULTI_TEXT]: [{ label: 'contains', operator: 'contains', takesValue: true }, IS_EMPTY],
  [CustomFieldType.DECIMAL]: [
    IS,
    { label: 'is greater than', operator: '>', takesValue: true },
    { label: 'is less than', operator: '<', takesValue: true },
    IS_EMPTY
  ],
  [CustomFieldType.INTEGER]: [
    IS,
    { label: 'is greater than', operator: '>', takesValue: true },
    { label: 'is less than', operator: '<', takesValue: true },
    IS_EMPTY
  ],
  [CustomFieldType.DATE]: [
    { label: 'is before', operator: '<', takesValue: true },
    { label: 'is after', operator: '>', takesValue: true },
    { label: 'is on', operator: '==', takesValue: true },
    IS_EMPTY
  ],
  [CustomFieldType.DATE_OFFSET]: [
    { label: 'is before', operator: '<', takesValue: true },
    { label: 'is after', operator: '>', takesValue: true },
    IS_EMPTY
  ],
  [CustomFieldType.BOOLEAN]: [IS],
  [CustomFieldType.FILE_UPLOAD]: [IS_PROVIDED, IS_EMPTY],
  [CustomFieldType.ARRAY]: [{ label: 'has at least', operator: '>=', takesValue: true }, IS_EMPTY],
  [CustomFieldType.CALCULATED]: [
    { label: 'is greater than', operator: '>', takesValue: true },
    { label: 'is less than', operator: '<', takesValue: true },
    IS_EMPTY
  ]
};

export const DEFAULT_OPERATORS: FriendlyOperator[] = [IS, IS_EMPTY];

export function operatorsFor(type: CustomFieldType | undefined): FriendlyOperator[] {
  return (type && OPERATORS_BY_TYPE[type]) || DEFAULT_OPERATORS;
}

export function newRule(caseTypeId?: string): LayoutRule {
  return {
    active: true,
    case_type_id: caseTypeId ?? null,
    conjunction: 'ALL',
    conditions: [{ field_key: null, operator: null }],
    action: RuleTargetAction.HIDE,
    targets: [],
    scope: 'EVERYWHERE',
    requiredness_gate: 'SUBMIT'
  };
}

/**
 * Save is gated with its reason. A rule that saves half-built is a rule that silently does nothing, and
 * nobody goes back to check.
 */
export function incompleteReason(rule: LayoutRule): string | null {
  if (!rule.conditions.length) return 'Add at least one condition.';
  if (rule.conditions.some(condition => !condition.field_key)) return 'Every condition needs a field.';
  if (
    rule.conditions.some(condition => {
      const friendly = findOperator(condition.operator);
      return friendly?.takesValue !== false && (condition.value === null || condition.value === undefined || condition.value === '');
    })
  ) {
    return 'Every condition needs a value.';
  }
  if (!rule.targets.length) return 'Choose at least one thing for the rule to act on.';
  return null;
}

function findOperator(operator: Operator | null): FriendlyOperator | undefined {
  if (!operator) return undefined;
  return Object.values(OPERATORS_BY_TYPE)
    .flat()
    .find(candidate => candidate?.operator === operator);
}

/** The live sentence under the builder. It folds in the scope, because scope changes what the rule does. */
export function describeRule(rule: LayoutRule, labelFor: (ref: string) => string = ref => ref.replace(/_/g, ' ')): string {
  const blocked = incompleteReason(rule);
  if (blocked) return blocked;

  const joiner = rule.conjunction === 'ALL' ? ' and ' : ' or ';
  const conditions = rule.conditions
    .map(condition => {
      const friendly = findOperator(condition.operator);
      const field = labelFor(condition.field_key ?? '');
      if (!friendly) return field;
      return friendly.takesValue ? `${field} ${friendly.label} ${formatValue(condition.value)}` : `${field} ${friendly.label}`;
    })
    .join(joiner);

  const verb =
    rule.action === RuleTargetAction.HIDE
      ? 'hide'
      : rule.action === RuleTargetAction.REQUIRE
        ? rule.requiredness_gate === 'WORKFLOW'
          ? 'need, to file,'
          : 'require'
        : rule.action === RuleTargetAction.OPTIONAL
          ? 'stop requiring'
          : rule.action === RuleTargetAction.SHOW
            ? 'show'
            : 'disable';

  const targets = rule.targets.map(target => labelFor(target.ref)).join(', ');
  const where = rule.scope === 'INTAKE' ? 'on the intake form' : rule.scope === 'CASE' ? 'on the case' : 'everywhere';

  return `When ${conditions}, ${verb} ${targets} ${where}.`;
}

function formatValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ');
  if (value === null || value === undefined) return '';
  // A condition value is a string, number, boolean or a list of them. Anything else would stringify to
  // "[object Object]" in the sentence the author is reading, which is worse than showing nothing.
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value.toString();
  return '';
}

/**
 * A rule as the case panel states it, on the section it acts on.
 *
 * Separate from {@link describeRule} because the panel is answering a different question. The builder's
 * sentence says what the rule *does* ("hide Claims everywhere"); the panel is looking at Claims already
 * and needs to know why it disappears and that this is the same rule the wizard obeys — otherwise the
 * layout gets blamed for the rule's behaviour.
 */
export interface PanelRuleNote {
  ruleId?: string;
  scopeLabel: string;
  sentence: string;
}

/** Rules that act on the case panel: everything except intake-only. Mirrors `LayoutRuleEvaluator.applies`. */
export function panelRules(rules: LayoutRule[]): LayoutRule[] {
  return (rules ?? []).filter(rule => rule.active !== false && (rule.scope === 'CASE' || rule.scope === 'EVERYWHERE'));
}

/**
 * Whether a rule acts on a section, or on a field inside it.
 *
 * Matched on the section **key**, never the title. Titles are author-editable and can collide, and an
 * eviction Section 8 rule firing on a delinquency section that happened to be called "Claims" was a real
 * bug in the prototype.
 */
export function ruleTouchesSection(rule: LayoutRule, sectionKey: string | undefined, fieldKeys: string[]): boolean {
  return rule.targets.some(target =>
    target.kind === RuleTargetType.SECTION
      ? !!sectionKey && target.ref === sectionKey
      : fieldKeys.includes(target.ref)
  );
}

export function describePanelRule(
  rule: LayoutRule,
  labelFor: (ref: string) => string = ref => ref.replace(/_/g, ' ')
): PanelRuleNote {
  const blocked = incompleteReason(rule);
  const conditions = rule.conditions
    .map(condition => {
      const friendly = findOperator(condition.operator);
      const field = labelFor(condition.field_key ?? '');
      if (!friendly) return field;
      return friendly.takesValue ? `${field} ${friendly.label} ${formatValue(condition.value)}` : `${field} ${friendly.label}`;
    })
    .join(rule.conjunction === 'ALL' ? ' and ' : ' or ');

  const effect =
    rule.action === RuleTargetAction.HIDE
      ? 'Hidden'
      : rule.action === RuleTargetAction.SHOW
        ? 'Shown'
        : rule.action === RuleTargetAction.REQUIRE
          ? 'Required'
          : rule.action === RuleTargetAction.OPTIONAL
            ? 'Not required'
            : 'Disabled';

  // The trailing clause is the point: it says the wizard and the panel are obeying one rule, not two
  // that happen to agree today.
  const because =
    rule.scope === 'EVERYWHERE'
      ? ' — the same rule that skips it at intake collapses it here.'
      : ' — asked at intake, then hidden here.';

  return {
    ruleId: rule.id,
    scopeLabel: RULE_SCOPE_LABELS[rule.scope],
    sentence: blocked ? blocked : `${effect} when ${conditions}${because}`
  };
}
